import { CoreModule, CoreMyModuleTypes, CoreFlowSyncModuleInfo, CoreCreateModule, CoreModuleView, CoreModuleRef } from '../models/CoreModels';
import { EmptyRequestState, RequestState } from '../models/RequestState';
import InstanceClient from '../services/InstanceClient';
import StatusCode from '../util/StatusCode';
import store from './store';
import { Dispatch } from "react";
import { Action, Reducer } from "redux";


enum ModuleType {
    GET_MODULES = 'GET_MODULES',
    GET_MODULE = 'GET_MODULE',
    MODULE_TYPES = 'MODULE_TYPES',
    MY_MODULE_TYPES = 'MY_MODULE_TYPES',
    MODULES_BY_TYPE_NAME = 'MODULES_BY_TYPE_NAME',
    MODULE_BASIC_INFO = 'MODULE_BASIC_INFO',
    CREATE_MODULE = 'CREATE_MODULE',
    UPDATE_MODULE = 'UPDATE_MODULE',
    GET_SYSTEMNAMES = 'GET_SYSTEMNAMES',
}

export interface ModuleState {
    modules: RequestState<CoreModule[]>
    module: RequestState<CoreModuleView>
    myModuleTypes: RequestState<CoreMyModuleTypes>
    moduleBasicInfo: RequestState<CoreModule>
    moduleTypes: RequestState<CoreFlowSyncModuleInfo[]>
    modulesByTypeName: RequestState<CoreModuleRef[]>
    createdModule: RequestState<CoreModule>
    updatedModule: EmptyRequestState
    systemNames: RequestState<string[]>
}

interface Module_GetModules extends Action<ModuleType> {
    type: ModuleType.GET_MODULES,
    modules: RequestState<CoreModule[]>
};

interface Module_GetModule extends Action<ModuleType> {
    type: ModuleType.GET_MODULE,
    module: RequestState<CoreModuleView>
};

interface Module_MyModuleTypes extends Action<ModuleType> {
    type: ModuleType.MY_MODULE_TYPES,
    myModuleTypes: RequestState<CoreMyModuleTypes>
};

interface Module_ModuleTypes extends Action<ModuleType> {
    type: ModuleType.MODULE_TYPES,
    moduleTypes: RequestState<CoreFlowSyncModuleInfo[]>
};

interface Module_ModulesByTypeName extends Action<ModuleType> {
    type: ModuleType.MODULES_BY_TYPE_NAME,
    modulesByTypeName: RequestState<CoreModuleRef[]>
};

interface Module_ModuleBasicInfo extends Action<ModuleType> {
    type: ModuleType.MODULE_BASIC_INFO,
    moduleBasicInfo: RequestState<CoreModule>
}

interface Module_CreateModule extends Action<ModuleType> {
    type: ModuleType.CREATE_MODULE,
    createdModule: RequestState<CoreModule>
}

interface Module_UpdateModule extends Action<ModuleType> {
    type: ModuleType.UPDATE_MODULE,
    updatedModule: EmptyRequestState
}

interface Module_SystemNames extends Action<ModuleType> {
    type: ModuleType.GET_SYSTEMNAMES,
    systemNames: RequestState<string[]>
}

type ModuleAction =
    Module_GetModules
    | Module_GetModule
    | Module_MyModuleTypes
    | Module_ModulesByTypeName
    | Module_ModuleTypes
    | Module_ModuleBasicInfo
    | Module_CreateModule
    | Module_UpdateModule
    | Module_SystemNames;


export const getModules = (update: boolean = false) => (dispatch: Dispatch<Module_GetModules>) => {
    if (!update) {
        dispatch({
            type: ModuleType.GET_MODULES,
            modules: { code: StatusCode.PENDING },
        })
    }
    return InstanceClient.get<CoreModule[]>(`/api/core/module`)
        .then(res => {
            dispatch({
                type: ModuleType.GET_MODULES,
                modules: { code: StatusCode.COMPLETE, data: res.data },
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.GET_MODULES,
                modules: { code: StatusCode.ERROR, error },
            });
        })
};

export const getModule = (id: string) => (dispatch: Dispatch<Module_GetModule>) => {
    dispatch({
        type: ModuleType.GET_MODULE,
        module: { code: StatusCode.PENDING },
    });
    return InstanceClient.get<CoreModuleView>(`/api/core/module/${id}`)
        .then(res => {
            dispatch({
                type: ModuleType.GET_MODULE,
                module: { code: StatusCode.COMPLETE, data: res.data },
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.GET_MODULE,
                module: { code: StatusCode.ERROR, error },
            });
        });
};

export const getMyModuleTypes = (dispatchPending: boolean = true) => (dispatch: Dispatch<Module_MyModuleTypes>) => {
    if (dispatchPending) {
        dispatch({
            type: ModuleType.MY_MODULE_TYPES,
            myModuleTypes: { code: StatusCode.PENDING },
        });
    }
    return InstanceClient.get<CoreMyModuleTypes>(`/api/core/module/myModuleTypes`)
        .then(res => {
            dispatch({
                type: ModuleType.MY_MODULE_TYPES,
                myModuleTypes: { code: StatusCode.COMPLETE, data: res.data },
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.MY_MODULE_TYPES,
                myModuleTypes: { code: StatusCode.ERROR, error },
            });
        });
};

export const getModuleTypes = () => (dispatch: Dispatch<Module_ModuleTypes>) => {
    dispatch({
        type: ModuleType.MODULE_TYPES,
        moduleTypes: { code: StatusCode.PENDING },
    });
    return InstanceClient.get<CoreFlowSyncModuleInfo[]>(`/api/core/module/moduletypes`)
        .then(res => {
            dispatch({
                type: ModuleType.MODULE_TYPES,
                moduleTypes: { code: StatusCode.COMPLETE, data: res.data },
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.MODULE_TYPES,
                moduleTypes: { code: StatusCode.ERROR, error },
            });
        });
};

export const getModulesByTypeName = (moduleTypeName: string) => (dispatch: Dispatch<Module_ModulesByTypeName>) => {
    dispatch({
        type: ModuleType.MODULES_BY_TYPE_NAME,
        modulesByTypeName: { code: StatusCode.PENDING },
    });
    return InstanceClient.get<CoreModuleRef[]>(`/api/core/module/moduleTypeByName/${moduleTypeName}`)
        .then(res => {
            dispatch({
                type: ModuleType.MODULES_BY_TYPE_NAME,
                modulesByTypeName: { code: StatusCode.COMPLETE, data: res.data },
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.MODULES_BY_TYPE_NAME,
                modulesByTypeName: { code: StatusCode.ERROR, error },
            });
        });
};

export const updateModule = (module: CoreModuleView) => (dispatch: Dispatch<Module_UpdateModule>) => {
    dispatch({
        type: ModuleType.UPDATE_MODULE,
        updatedModule: { code: StatusCode.PENDING }
    });
    return InstanceClient.put<CoreModuleView, CoreModuleView>(`/api/core/module/${module.id}`, module)
        .then(response => {
            dispatch({
                type: ModuleType.UPDATE_MODULE,
                updatedModule: { code: StatusCode.COMPLETE }
            });
            return response;
        })
        .catch(error => {
            dispatch({
                type: ModuleType.UPDATE_MODULE,
                updatedModule: { code: StatusCode.ERROR, error }
            });
            return null;
        })
        .finally(() => {
            setTimeout(() => {
                dispatch({
                    type: ModuleType.UPDATE_MODULE,
                    updatedModule: { code: StatusCode.NONE }
                });
            }, 1500);
        });
};

export const createModule = (module: CoreCreateModule) => (dispatch: Dispatch<Module_CreateModule>) => {
    dispatch({
        type: ModuleType.CREATE_MODULE,
        createdModule: { code: StatusCode.PENDING }
    })
    return InstanceClient.post<CoreModule, CoreCreateModule>(`/api/core/module`, module)
        .then(response => {
            dispatch({
                type: ModuleType.CREATE_MODULE,
                createdModule: { code: StatusCode.COMPLETE, data: response.data }
            });
            return response.data;
        })
        .catch(error => {
            dispatch({
                type: ModuleType.CREATE_MODULE,
                createdModule: { code: StatusCode.ERROR, error }
            });
            return null;
        });
};

export const clearNewModule = () => (dispatch: Dispatch<Module_CreateModule>) => {
    dispatch({
        type: ModuleType.CREATE_MODULE,
        createdModule: { code: StatusCode.NONE }
    });
};

export const getModuleBasicInfo = (id: string) => (dispatch: Dispatch<Module_ModuleBasicInfo>) => {
    const storeKey = store.getState().module.moduleBasicInfo.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    return InstanceClient.get<CoreModule>(`/api/core/module/${id}/basicInfo`)
        .then(res =>
            dispatch({ type: ModuleType.MODULE_BASIC_INFO, moduleBasicInfo: { code: StatusCode.COMPLETE, data: res.data, key: key } }))
        .catch(error =>
            dispatch({ type: ModuleType.MODULE_BASIC_INFO, moduleBasicInfo: { code: StatusCode.ERROR, error } }));
}

export const resetModuleBasicInfo = () => (dispatch: Dispatch<Module_ModuleBasicInfo>) => {
    dispatch({
        type: ModuleType.MODULE_BASIC_INFO,
        moduleBasicInfo: { code: StatusCode.NONE }
    });
};

export const getSystemNames = () => (dispatch: Dispatch<Module_SystemNames>) => {
    dispatch({
        type: ModuleType.GET_SYSTEMNAMES,
        systemNames: { code: StatusCode.PENDING }
    });
    return InstanceClient.get<string[]>(`/api/core/module/systemNames`)
        .then(res => {
            dispatch({
                type: ModuleType.GET_SYSTEMNAMES,
                systemNames: { code: StatusCode.COMPLETE, data: res.data }
            });
        })
        .catch(error => {
            dispatch({
                type: ModuleType.GET_SYSTEMNAMES,
                systemNames: { code: StatusCode.ERROR, error }
            });
        });
};

export const resetUpdateModule = () => (dispatch: Dispatch<Module_UpdateModule>) => {
    dispatch({
        type: ModuleType.UPDATE_MODULE,
        updatedModule: { code: StatusCode.NONE }
    });
};

export const resetGetModule = () => (dispatch: Dispatch<Module_GetModule>) => {
    dispatch({
        type: ModuleType.GET_MODULE,
        module: { code: StatusCode.NONE },
    });
};

const initialState: ModuleState = {
    modules: { code: StatusCode.NONE },
    module: { code: StatusCode.NONE },
    myModuleTypes: { code: StatusCode.NONE },
    modulesByTypeName: { code: StatusCode.NONE },
    moduleBasicInfo: { code: StatusCode.NONE },
    moduleTypes: { code: StatusCode.NONE },
    createdModule: { code: StatusCode.NONE },
    updatedModule: { code: StatusCode.NONE },
    systemNames: { code: StatusCode.NONE },
};

export const reducer: Reducer<ModuleState, ModuleAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case ModuleType.GET_MODULES:
            return {
                ...state,
                modules: action.modules
            };
        case ModuleType.GET_MODULE:
            return {
                ...state,
                module: action.module
            };
        case ModuleType.CREATE_MODULE:
            return {
                ...state,
                createdModule: action.createdModule
            };
        case ModuleType.MY_MODULE_TYPES:
            return {
                ...state,
                myModuleTypes: action.myModuleTypes
            };
        case ModuleType.MODULES_BY_TYPE_NAME:
            return {
                ...state,
                modulesByTypeName: action.modulesByTypeName
            };
        case ModuleType.MODULE_TYPES:
            return {
                ...state,
                moduleTypes: action.moduleTypes
            };
        case ModuleType.UPDATE_MODULE:
            return {
                ...state,
                updatedModule: action.updatedModule
            };
        case ModuleType.GET_SYSTEMNAMES:
            return {
                ...state,
                systemNames: action.systemNames
            };
        case ModuleType.MODULE_BASIC_INFO:
            return {
                ...state,
                moduleBasicInfo: action.moduleBasicInfo
            };
        default:
            return state;
    }
}
